import { Directive, ElementRef, HostListener, inject, Input, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { AuthService } from 'src/app/auth.service';
import { DataService } from 'src/app/data.service';
import { ValidationService } from '../../validation.service';
import { ProjectService } from 'src/app/project.service';
import { AufnahmePunktDocument } from 'src/app/data-model';

type SomeTableElement = HTMLTableColElement | HTMLTableRowElement | HTMLTableCellElement;

@Directive({
    standalone: false,
    selector: '[bpHl]',
    
})
export class HlAreaDirective implements OnInit, OnDestroy {

  @Input() bpHl = 'componentName';
  @Input() hldata: any;

	private _enabled: boolean | null = null;
	private _isPrintView: boolean | null = null;
	private pointSavedSub?: Subscription;

  private myClass = () => `highlight-area-${this.bpHl}`;

	// Lazy evaluation: Berechtigung wird bei erstem Zugriff geprüft
	private get enabled(): boolean {
		if (this._enabled === null) {
			if (this._isPrintView === null) {
				this._isPrintView = this.isPartOfPrintView();
			}
			// canIWritePoint kombiniert canI('write', 'points') mit Frozen-Lock —
			// bei abgeschlossenem Punkt im abgeschlossenen Projekt darf niemand klicken.
			this._enabled = !this._isPrintView
				&& this.ps.canIWritePoint(this.hldata)
				&& this.isAssignedToActiveProject();
			// CSS-Klassen nachträglich setzen falls nötig
			this.updateCssClasses();
		}
		return this._enabled;
	}

  private aus = inject(AuthService);
  private el = inject<ElementRef<SomeTableElement>>(ElementRef);
  private vs = inject(ValidationService);
  private router = inject(Router);
  private ps = inject(ProjectService);
  private ds = inject(DataService);

  ngOnInit(): void {
		// CSS-Klassen werden lazy im getter gesetzt
		// Trigger den getter einmal um die Klassen zu setzen falls Berechtigung schon da ist
		const _ = this.enabled;

		// Bei Punkt-Speicherung: Cache invalidieren und neu prüfen
		this.pointSavedSub = this.ds.pointSaved.subscribe((savedBp: AufnahmePunktDocument) => {
			if (savedBp._id === this.hldata?._id) {
				this._enabled = null;
				// Aktualisiere hldata mit neuen Daten
				if (this.hldata) {
					this.hldata.header.projects = savedBp.header.projects;
				}
				// Trigger Neuberechnung und CSS-Update
				const _ = this.enabled;
			}
		});
  }

  ngOnDestroy(): void {
		this.pointSavedSub?.unsubscribe();
  }

  private updateCssClasses(): void {
		if (this._enabled) {
			this.el.nativeElement.classList.add('highlight-area');
			this.el.nativeElement.classList.add(this.myClass());
		} else {
			this.el.nativeElement.classList.remove('highlight-area');
			this.el.nativeElement.classList.remove(this.myClass());
		}
  }

  /**
   * Prüft ob der Punkt dem aktiven Projekt zugewiesen ist.
   * Ohne geöffnetes Projekt ist keine Bearbeitung möglich.
   */
  private isAssignedToActiveProject(): boolean {
    // Single-Source in ProjectService (zuvor 3× inline reimplementiert).
    return this.ps.isPointAssignedToActiveProject(this.hldata);
  }

	isPartOfPrintView() {
		const checkThis = (el: HTMLElement) => el.classList.contains('bp-print-view');
		const check = (el: HTMLElement): boolean => {
			if (checkThis(el)) return true;
			return el.parentElement !== undefined && el.parentElement !== null && check(el.parentElement);
		};
		return check(this.el.nativeElement);
	}
	
  @HostListener('mouseenter') onMouseEnter() {
    if (this.enabled) this.highlight(true);
  }

  @HostListener('mouseleave') onMouseLeave() {
    if (this.enabled) this.highlight(false);
  }

  @HostListener('click') onClick() {
    if (this.enabled) this.highlight(false);
    if (this.enabled) this.router.navigate(['/bohrpunkte', this.hldata._id, this.bpHl]);
  }

  private highlightElementRef(elem: ElementRef<SomeTableElement>, on: boolean) {
    const e = elem.nativeElement;
		this.highlightElement(e, on);
  }

  private highlightElement(elem: SomeTableElement, on: boolean) {
    if (on) elem.classList.add("highlight-area-hover");
    else elem.classList.remove("highlight-area-hover");
  }

  private belongsToThis(elem: SomeTableElement) {
    return elem.classList.contains(this.myClass());
  }

  private highlight(on: boolean) {
    // highlighte das aktuelle Element
    this.highlightElementRef(this.el, on);
    const thisEl = this.el.nativeElement;
    let p: HTMLElement | null | undefined = thisEl.parentElement;
    if (p && thisEl instanceof HTMLTableColElement || thisEl instanceof HTMLTableCellElement) p = p?.parentElement;
    if (!p || !p.children) return;

    // highlight aller Geschwisterelemente, die zur gleichen Klasse gehören
    const highlight = (e?: Element | null) => {
      if (!e) return;
      if (!(e instanceof HTMLTableRowElement ||
        e instanceof HTMLTableColElement ||
        e instanceof HTMLTableCellElement)) return;
      const elem = e as SomeTableElement;
      if (this.belongsToThis(elem)) this.highlightElement(elem, on);
    };
    const highlightChildren = (e: Element | null) => {
      highlight(e);
      if (!e) return;
      for (let i = 0; i < e.childElementCount; ++i)
        highlightChildren(e.children.item(i));
    };
    highlightChildren(p);
  }
}
